/* eslint-disable jsx-a11y/iframe-has-title */
import { memo } from 'react';
import { renderToString } from 'react-dom/server';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { isNil } from 'lodash';
// Import Components
import { HooryPreview, PushPreview, TelegramPreview, ViberPreview } from './Preview';
import WhatsAppTemplatePreview from 'components/Template/WhatsApp/WhatsAppTemplatePreview/WhatsAppTemplatePreview';
// Import Constants
import {
    l,
    DisplayModeStyles,
    TemplateTypesLabels,
    DisplayModeClassNames,
    DeviceTypeClassNames,
    DynamicPopUpDeviceTypesLabels,
} from 'constants/common';
// Import Services
import { Helpers } from 'services';
// Import Hooks
import { useWhatsAppPreview } from './Preview/UseWhatsAppPreview';

const { convertMinutesToHHMM } = Helpers;

const TemplateInfoTab = ({ body, subject, isSubjectVisible, configs, deliveryMethod }) => {
    const { t } = useTranslation();

    const whatsAppTemplate = useWhatsAppPreview({
        body,
        enabled: deliveryMethod === TemplateTypesLabels.WhatsApp,
    });

    const getSubContent = (deliveryMethod, body) => {
        if (
            isSubjectVisible &&
            ![
                TemplateTypesLabels.Viber,
                TemplateTypesLabels.Hoory,
                TemplateTypesLabels.WhatsApp,
                TemplateTypesLabels.Telegram,
                TemplateTypesLabels.DynamicPopUp,
                TemplateTypesLabels.PushNotification,
            ].includes(deliveryMethod)
        ) {
            return [{ label: t(l.Subject), value: subject }];
        } else if (deliveryMethod === TemplateTypesLabels.Hoory) {
            const ValidityPeriod = JSON.parse(body)?.Schedule?.ValidityPeriod;
            return [
                { label: t(l.Subject), value: subject },
                {
                    label: t(l.ValidityPeriod),
                    value: isNil(ValidityPeriod) ? t(l.NA) : convertMinutesToHHMM(ValidityPeriod),
                },
            ];
        } else if (deliveryMethod === TemplateTypesLabels.Viber) {
            const ValidityPeriod = JSON.parse(body)?.Schedule?.ValidityPeriod;
            return [
                {
                    label: t(l.ValidityPeriod),
                    value: isNil(ValidityPeriod) ? t(l.NA) : convertMinutesToHHMM(ValidityPeriod),
                },
            ];
        } else if (deliveryMethod === TemplateTypesLabels.WhatsApp) {
            return [
                {
                    label: t(l.Status),
                    value: whatsAppTemplate?.isLoading ? t(l.Loading) : t(whatsAppTemplate?.status ?? l.NA),
                },
            ];
        } else if (deliveryMethod === TemplateTypesLabels.DynamicPopUp) {
            const parsedBody = JSON.parse(body);
            const deviceType = t(DynamicPopUpDeviceTypesLabels[parsedBody.Platforms]);
            const urlTypes = configs.urlTypesData;
            const urlType = urlTypes.find((item) => item.value === parsedBody.Page.Comparison)?.label;

            return [
                { label: t(l.UrlType), value: urlType ?? t(l.NA) },
                { label: t(l.Url), value: parsedBody.Page.Value },
                { label: t(l.DeviceType), value: t(deviceType) },
            ];
        } else if (deliveryMethod === TemplateTypesLabels.PushNotification) {
            const parsedBody = JSON.parse(body);
            return [{ label: t(l.Subject), value: parsedBody.message_title || subject }];
        } else {
            return [];
        }
    };

    const getKeyValueGroup = (data) => {
        if (data.length === 0) {
            return null;
        }

        return (
            <>
                {data.map(({ label, value }, index) => (
                    <div key={index} className="key-value-h">
                        <span className="ellipsis-text kv-label">{label}</span>
                        <span className="ellipsis-text kv-value kv-value-css">{value}</span>
                    </div>
                ))}
            </>
        );
    };

    const getBody = (deliveryMethod, body) => {
        if (deliveryMethod === TemplateTypesLabels.Viber) {
            const viberTemplate = JSON.parse(body);

            const button = viberTemplate?.InlineKeyboard?.[0]?.[0] ?? { Text: '', Url: '' };

            return renderToString(
                <ViberPreview
                    text={viberTemplate.Body}
                    buttonText={button.Text}
                    buttonUrl={button.Url}
                    mediaUrl={viberTemplate.ImageUrl}
                    displayMode={configs?.modeConfig?.type}
                />,
            );
        } else if (deliveryMethod === TemplateTypesLabels.Hoory) {
            const hooryTemplate = JSON.parse(body);

            const button = hooryTemplate?.InlineKeyboard?.[0]?.[0] ?? { Text: '', Url: '' };

            return renderToString(
                <HooryPreview
                    text={hooryTemplate.Body}
                    buttonText={button.Text}
                    buttonUrl={button.Url}
                    mediaUrl={hooryTemplate.ImageUrl}
                    displayMode={configs?.modeConfig?.type}
                />,
            );
        } else if (deliveryMethod === TemplateTypesLabels.Telegram) {
            const telegramTemplate = JSON.parse(body);

            return renderToString(
                <TelegramPreview
                    text={telegramTemplate.Text}
                    buttons={telegramTemplate.InlineKeyboard}
                    mediaUrl={telegramTemplate.MediaUrl}
                    displayMode={configs?.modeConfig?.type}
                    displaySize={configs?.bodyConfig?.type}
                />,
            );
        } else if (deliveryMethod === TemplateTypesLabels.PushNotification) {
            const pushTemplate = JSON.parse(body);

            return renderToString(
                <PushPreview messageBody={pushTemplate.message_body} imageUrl={pushTemplate.extra_info?.image_url} />,
            );
        } else if (deliveryMethod === TemplateTypesLabels.DynamicPopUp) {
            return JSON.parse(body).Content;
        } else if (deliveryMethod === TemplateTypesLabels.WhatsApp) {
            return renderToString(
                <WhatsAppTemplatePreview
                    components={whatsAppTemplate.components}
                    displayMode={configs?.modeConfig?.type}
                    isLoading={whatsAppTemplate.isLoading}
                />,
            );
        } else {
            return body;
        }
    };

    return (
        <div className="inner-wrap-tabs-holder-info">
            {getKeyValueGroup(getSubContent(deliveryMethod, body))}
            <div className={DisplayModeClassNames[configs?.modeConfig?.type]}>
                <div className={DeviceTypeClassNames[configs?.bodyConfig?.type]}>
                    {/* The body is authored by other users. Without `sandbox` a srcDoc frame runs in the app
                        origin and can read the access token. Never add `allow-scripts` or `allow-same-origin`. */}
                    <iframe
                        id={configs?.bodyConfig?.titleKey}
                        title={configs?.bodyConfig?.titleKey}
                        sandbox="allow-popups allow-popups-to-escape-sandbox"
                        style={{ height: 300 }}
                        srcDoc={`<style>${DisplayModeStyles[configs?.modeConfig?.type]}</style>${getBody(deliveryMethod, body)}`}
                    />
                </div>
            </div>
        </div>
    );
};

TemplateInfoTab.propTypes = {
    body: PropTypes.string.isRequired,
    configs: PropTypes.object.isRequired,
    subject: PropTypes.string,
    isSubjectVisible: PropTypes.bool,
    deliveryMethod: PropTypes.number.isRequired,
};

TemplateInfoTab.defaultProps = {
    subject: '',
    isSubjectVisible: true,
};

export default memo(TemplateInfoTab);
